import { Fragment, useRef, useState, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XIcon, RefreshIcon } from '@heroicons/react/outline'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { Pagination } from 'antd'
import { Loader } from '../../../components/Loader'
import { fetchSalesChannelHistory } from '../../../services'
import { formatDate as formatDateUtil } from '../../CubboLastmile/utils'
import { ACTION_STYLES, getLinkChanges } from './utils'

const T_PREFIX = 'sales_channels'

export const SalesChannelHistoryModal = ({ open, setOpen, channel = null, warehouseTimeZone = null }) => {
  const { i18n } = useTranslation()
  const cancelButtonRef = useRef(null)
  const [page, setPage] = useState(1)

  useEffect(() => {
    setPage(1)
  }, [channel?.id])

  const { data, isLoading, isFetching, refetch } = useQuery(
    ['sales_channel_history', channel?.id, page],
    () => fetchSalesChannelHistory(channel.id, { page, per_page: 10 }),
    // No keepPreviousData: on an audit surface, showing a loader beats attributing the
    // previous channel's history to the newly-opened one while its fetch is in flight.
    { enabled: open && !!channel?.id, refetchOnWindowFocus: false },
  )

  const histories = data?.sales_channel_histories || []
  const meta = data?.meta || {}

  const getActionLabel = action => i18n.t(`${T_PREFIX}.history.action_${action}`)
  const formatDate = dateStr => formatDateUtil(dateStr, i18n.language, warehouseTimeZone)

  const handleClose = () => {
    setOpen(false)
    setPage(1)
  }

  const renderChanges = entry => {
    if (entry.action === 'name_update') {
      return (
        <div className="flex items-center gap-1 flex-wrap text-sm">
          <span className="text-red-500 line-through">{entry.before_change?.name || '-'}</span>
          <span className="text-gray-400">&rarr;</span>
          <span className="text-green-600">{entry.after_change?.name || '-'}</span>
        </div>
      )
    }
    if (entry.action === 'links_update') {
      const { added, removed } = getLinkChanges(entry)
      return (
        <div className="space-y-1 text-sm">
          {added.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap">
              <span className="font-medium text-gray-700">{i18n.t(`${T_PREFIX}.history.added`)}:</span>
              {added.map(l => (
                <span key={`a-${l.id}`} className="bg-green-100 text-green-700 rounded px-2 py-0.5 text-xs">
                  {l.name}
                </span>
              ))}
            </div>
          )}
          {removed.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap">
              <span className="font-medium text-gray-700">{i18n.t(`${T_PREFIX}.history.removed`)}:</span>
              {removed.map(l => (
                <span key={`r-${l.id}`} className="bg-red-100 text-red-700 rounded px-2 py-0.5 text-xs line-through">
                  {l.name}
                </span>
              ))}
            </div>
          )}
        </div>
      )
    }
    return <span className="text-gray-400 text-sm">-</span>
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" initialFocus={cancelButtonRef} onClose={handleClose}>
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-visible shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                  {i18n.t(`${T_PREFIX}.history.title`)}
                  {channel?.name ? ` - ${channel.name}` : ''}
                </Dialog.Title>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    className="text-gray-400 hover:text-blue-600 transition-colors"
                    onClick={() => refetch()}
                    disabled={isFetching}
                    title={i18n.t(`${T_PREFIX}.history.refresh`)}
                  >
                    <RefreshIcon className={`h-5 w-5 ${isFetching ? 'animate-spin' : ''}`} />
                  </button>
                  <button type="button" className="text-gray-400 hover:text-gray-500" onClick={handleClose} ref={cancelButtonRef}>
                    <XIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="relative min-h-[200px]">
                {isLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader show={true} />
                  </div>
                ) : histories.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">{i18n.t(`${T_PREFIX}.history.no_history`)}</p>
                  </div>
                ) : (
                  <>
                    {isFetching && <div className="absolute inset-0 bg-white bg-opacity-50 z-10 pointer-events-none" />}
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{i18n.t(`${T_PREFIX}.history.action`)}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{i18n.t(`${T_PREFIX}.history.changes`)}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{i18n.t(`${T_PREFIX}.history.user`)}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{i18n.t(`${T_PREFIX}.history.date`)}</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {histories.map(entry => (
                            <tr key={entry.id}>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${ACTION_STYLES[entry.action] || 'bg-gray-100 text-gray-800'}`}
                                >
                                  {getActionLabel(entry.action)}
                                </span>
                              </td>
                              <td className="px-4 py-3">{renderChanges(entry)}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{entry.created_by?.email || '-'}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatDate(entry.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {meta.total_pages > 1 && (
                      <div className="flex justify-end mt-4">
                        <Pagination
                          size="small"
                          current={meta.current_page}
                          total={meta.total_count}
                          pageSize={10}
                          onChange={p => setPage(p)}
                          showSizeChanger={false}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
